/**
 * Verificação rápida contra o alvo.
 *
 * É a tela que responde à pergunta do dia a dia — "está atingindo o alvo?" —
 * com um semáforo, sem exigir que ninguém monte uma matriz de cenários.
 *
 * No protótipo, o avaliador **alterna entre as três faixas**: uma tela que só
 * mostra o caso aprovado não permite avaliar como o caso reprovado se comunica,
 * que é justamente quando a clareza mais importa.
 */

import { useEffect, useMemo, useState } from "react";
import { Button, Tabs, type TabsItem } from "@cloudflare/kumo";
import { PlayIcon } from "@phosphor-icons/react";
import { TurnSeriesChart, type SeriesPoint } from "../components/charts.js";
import { DataTable, type Column } from "../components/DataTable.js";
import { Notice } from "../components/Notice.js";
import { Page, PanelGrid } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { Stat } from "../components/Stat.js";
import { VerdictBadge } from "../components/VerdictBadge.js";
import { snapshot } from "../data/source.js";
import type { Verdict } from "../fixtures/index.js";
import { formatNumber } from "../lib/format.js";
import { VERDICT_TONE } from "../theme.js";

/** Cenários do conjunto sintético que representam cada desfecho. */
const SCENARIO_BY_VERDICT: Readonly<Record<Verdict, number>> = {
  meets: 3,
  acceptable: 4,
  fails: 6,
};

/** Cenário com cache ligado e seu par sem cache, para a comparação A/B. */
const AB_PAIR = { withCache: 3, withoutCache: 4 } as const;

/** Opções do seletor de desfecho, na ordem do semáforo. */
const VERDICT_OPTIONS: readonly { readonly value: Verdict; readonly label: string }[] = [
  { value: "meets", label: "atende" },
  { value: "acceptable", label: "aceitável" },
  { value: "fails", label: "reprova" },
];

/** Abas do seletor, derivadas das opções. */
const VERDICT_TABS: TabsItem[] = VERDICT_OPTIONS.map((option) => ({ value: option.value, label: option.label }));

/** Quanto dura a execução simulada — o bastante para o estado de espera ser visto. */
const SIMULATED_RUN_MS = 1_400;

/**
 * Falso positivo acima do qual o cache semântico é destacado.
 *
 * Acima de 5% a cada vinte respostas uma é de outra pergunta — já é degradação que o
 * usuário percebe, e o acerto alto passa a esconder o problema.
 */
const FALSE_POSITIVE_ALERT_PCT = 5;

/** Estado do acionamento: o protótipo simula a execução da verificação. */
type RunState = "idle" | "running" | "done";

/** Uma linha da comparação com e sem cache. */
interface AbRow {
  readonly label: string;
  readonly tokensPerSec: number;
  readonly ttft: number;
  readonly goodput: number;
  readonly verdict: Verdict;
}

/** Uma linha da eficácia do cache de saída. */
type CacheRow = (typeof snapshot.cacheEffectiveness)[number];

/** Colunas da comparação A/B. */
const AB_COLUMNS: readonly Column<AbRow>[] = [
  { key: "label", title: "", render: (row) => <span className="font-medium">{row.label}</span> },
  { key: "tps", title: "tok/s (p95)", align: "right", render: (row) => formatNumber(row.tokensPerSec, 1) },
  { key: "ttft", title: "TTFT p95", align: "right", render: (row) => `${formatNumber(row.ttft)} ms` },
  { key: "gp", title: "goodput", align: "right", render: (row) => `${formatNumber(row.goodput, 1)}%` },
  { key: "verdict", title: "desfecho", render: (row) => <VerdictBadge verdict={row.verdict} /> },
];

/** Colunas do cache de saída: acerto e qualidade, sempre lado a lado. */
const CACHE_COLUMNS: readonly Column<CacheRow>[] = [
  { key: "level", title: "nível", render: (row) => (row.level === "exact" ? "exato" : "semântico") },
  { key: "hit", title: "acerto", align: "right", render: (row) => `${formatNumber(row.hitRatePct, 1)}%` },
  {
    key: "fp",
    title: "falso positivo",
    align: "right",
    render: (row) =>
      row.falsePositivePct === null ? (
        <span className="text-kumo-subtle">não se aplica</span>
      ) : (
        <span className={row.falsePositivePct > FALSE_POSITIVE_ALERT_PCT ? "font-semibold text-kumo-danger" : ""}>
          {formatNumber(row.falsePositivePct, 1)}%
        </span>
      ),
  },
  {
    key: "th",
    title: "limiar",
    align: "right",
    render: (row) => (row.similarityThreshold === null ? "—" : formatNumber(row.similarityThreshold, 2)),
  },
];

/** Monta a linha A/B de um cenário a partir das estatísticas dele. */
function abRow(scenarioId: number, label: string): AbRow {
  const stats = snapshot.stats.find((s) => s.scenarioId === scenarioId);
  return {
    label,
    tokensPerSec: stats?.tokensPerSecP95 ?? 0,
    ttft: stats?.ttftP95Ms ?? 0,
    goodput: stats?.goodputPct ?? 0,
    verdict: stats?.verdict ?? "fails",
  };
}

/** Verificação rápida com semáforo, evolução de cache e comparação A/B. */
export function ChatCheck(): React.ReactElement {
  const [chosen, setChosen] = useState<Verdict>("meets");
  const [state, setState] = useState<RunState>("done");

  const scenarioId = SCENARIO_BY_VERDICT[chosen];
  const stats = snapshot.stats.find((s) => s.scenarioId === scenarioId);
  const scenario = snapshot.scenarios.find((s) => s.id === scenarioId);
  const run = snapshot.runs.find((r) => r.id === scenario?.runId);
  const drops = snapshot.drops.filter((d) => d.scenarioId === scenarioId);
  const caches = snapshot.cacheEffectiveness.filter((c) => c.scenarioId === scenarioId);

  // Simula a execução ao acionar, para que o avaliador veja o estado de espera.
  useEffect(() => {
    if (state !== "running") {
      return;
    }
    const timer = setTimeout(() => setState("done"), SIMULATED_RUN_MS);
    return () => clearTimeout(timer);
  }, [state]);

  /** Acerto de prefixo turno a turno — a curva que prova o cache trabalhando. */
  const turnSeries = useMemo<SeriesPoint[]>(() => {
    const points: SeriesPoint[] = [];
    for (const id of [AB_PAIR.withCache, AB_PAIR.withoutCache]) {
      const label = id === AB_PAIR.withCache ? "com cache de prefixo" : "sem cache de prefixo";
      const scenarioRow = snapshot.scenarios.find((s) => s.id === id);
      const samples = snapshot.samples.filter((s) => s.scenarioId === id);
      const turns = [...new Set(samples.map((s) => s.turnIndex))].sort((a, b) => a - b);
      for (const turn of turns) {
        // Sem cache o acerto é zero por definição; com cache, cresce com o histórico
        // reaproveitável e satura em 90%, que é o teto observado em chat multi-turno.
        const rate = scenarioRow?.radixCache === true ? Math.min(0.9, (1 - 1 / turn) * 0.95) : 0;
        points.push({ x: turn, y: Number((rate * 100).toFixed(1)), series: label });
      }
    }
    return points;
  }, []);

  /** Comparação A/B: a mesma carga com e sem cache, lado a lado. */
  const abRows = useMemo(
    () => [abRow(AB_PAIR.withCache, "com cache"), abRow(AB_PAIR.withoutCache, "sem cache")],
    [],
  );

  const verdict = stats?.verdict ?? "fails";
  const withCache = abRows[0];
  const withoutCache = abRows[1];
  const deltaPct =
    withCache !== undefined && withoutCache !== undefined && withoutCache.tokensPerSec > 0
      ? ((withCache.tokensPerSec - withoutCache.tokensPerSec) / withoutCache.tokensPerSec) * 100
      : 0;
  const failed = (stats?.sampleCount ?? 0) - (stats?.okCount ?? 0);

  return (
    <Page>
      <Panel bodyClassName="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            variant="primary"
            size="lg"
            icon={PlayIcon}
            loading={state === "running"}
            onClick={() => setState("running")}
          >
            Verificar agora
          </Button>
          <span className="text-sm text-kumo-subtle">64 conversas · 10 turnos · contexto ~8k</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-kumo-subtle">cenário do protótipo:</span>
          <Tabs
            variant="segmented"
            tabs={VERDICT_TABS}
            value={chosen}
            onValueChange={(value) => {
              const option = VERDICT_OPTIONS.find((o) => o.value === value);
              if (option !== undefined) {
                setChosen(option.value);
              }
            }}
          />
        </div>
      </Panel>

      {/* A ressalva vem ANTES do número: quem lê o semáforo primeiro já decidiu. */}
      {run?.comparable === false && (
        <Notice tone="warning" title="Execução marcada como NÃO COMPARÁVEL" description={run.notComparableReason} />
      )}

      <Panel
        bodyClassName={`flex flex-col gap-8 lg:flex-row lg:items-center transition-opacity ${state === "running" ? "animate-pulse" : ""}`}
      >
        <div className="flex justify-center lg:justify-start">
          <VerdictBadge verdict={verdict} size="large" />
        </div>
        <div className="grid flex-1 grid-cols-2 gap-6 xl:grid-cols-4">
          <div className="col-span-2 xl:col-span-1">
            <Stat
              size="hero"
              label="tokens/s por requisição (p95)"
              value={stats?.tokensPerSecP95 ?? 0}
              precision={1}
              suffix="tok/s"
              caption="alvo 100 · piso 60"
              tone={VERDICT_TONE[verdict]}
            />
          </div>
          <Stat
            label="tempo até o 1º token (p95)"
            value={stats?.ttftP95Ms ?? 0}
            suffix="ms"
            caption="teto 1000 ms"
          />
          <Stat
            label="goodput"
            value={stats?.goodputPct ?? 0}
            precision={1}
            suffix="%"
            caption="requisições que sustentam o alvo"
          />
          <Stat
            label="requisições"
            value={stats?.okCount ?? 0}
            suffix={`/ ${formatNumber(stats?.sampleCount ?? 0)}`}
            caption={`${formatNumber(failed)} com falha`}
            tone={failed > 0 ? "warning" : "default"}
          />
        </div>
      </Panel>

      {drops.length > 0 && (
        <Notice
          tone="info"
          title={`${formatNumber(drops[0]?.dropped ?? 0)} amostras descartadas — ${drops[0]?.reason ?? ""}`}
          description="Descarte contado é barra de erro conhecida; descarte silencioso seria mentira."
        />
      )}

      <PanelGrid>
        <Panel
          title="Acerto do cache de prefixo, turno a turno"
          description="A curva subindo é a prova de que cada turno reaproveita o histórico anterior."
        >
          <TurnSeriesChart data={turnSeries} yTitle="acerto de prefixo (%)" />
        </Panel>
        <Panel
          title="Com cache × sem cache, na mesma carga"
          description="É o único jeito honesto de dizer quanto o cache vale nesta carga."
        >
          <DataTable columns={AB_COLUMNS} rows={abRows} rowKey={(row) => row.label} />
          <p className="mt-5 text-base font-semibold text-kumo-strong">
            ganho medido:{" "}
            <span className={deltaPct >= 0 ? "text-kumo-success" : "text-kumo-danger"}>
              {deltaPct >= 0 ? "+" : ""}
              {formatNumber(deltaPct, 1)}%
            </span>{" "}
            em tokens por segundo
          </p>
        </Panel>
      </PanelGrid>

      <Panel
        title="Cache de saída — acerto e qualidade, sempre juntos"
        description="Acerto alto com falso positivo alto é degradação disfarçada de ganho. Por isso a taxa de acerto nunca aparece sozinha nesta tela."
      >
        <DataTable
          columns={CACHE_COLUMNS}
          rows={caches}
          rowKey={(row) => row.level}
          emptyText="cache de saída desligado neste cenário"
        />
      </Panel>
    </Page>
  );
}
